import Component from '@glimmer/component';
import AppHeaderInitialsIcon from 'tio-employee/components/app/header/initials-icon';
import { service } from '@ember/service';
import { Dropdown } from 'tio-ui/components/collections';
import { PhCaretRight, PhSignOut, PhUserCircle } from 'ember-phosphor-icons';
import { t } from 'ember-intl';
import { concat } from '@ember/helper';
import { eq, lowercase } from 'tio-ui/utilities';
import { subtract } from 'tio-ui/math';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import type SessionContextService from 'tio-employee/services/session-context';
import type RoleViewModel from 'tio-common/models/role-view';
import type Owner from '@ember/owner';
import { LinkTo } from '@ember/routing';

export default class SidebarUserMenuComponent extends Component {
  @service declare sessionContext: SessionContextService;
  @tracked currentRole?: RoleViewModel;

  constructor(owner: Owner, args: never) {
    super(owner, args);
    this.currentRole = this.sessionContext.currentRole!;
  }

  get activeRoles() {
    return this.sessionContext.roles.filter((role) => role.isActive);
  }

  get user() {
    return this.sessionContext.user;
  }

  setCurrentRole = (role: RoleViewModel) => {
    this.sessionContext.setCurrentRole(role);
    this.currentRole = role;
  };

  get isFamilyMember() {
    return this.sessionContext.isFamilyMember;
  }

  <template>
    {{! Mobile (show static list instead of dropdown) }}
    <div class="lg:hidden mb-4">
      <div class="mb-2 w-full text-sm text-white text-left px-1 py-1">
        <div class="flex items-center font-normal">
          <AppHeaderInitialsIcon @initials={{this.user.person.initials}} @size="sm" />
          <div class="align-middle ml-2 max-w-[140px] truncate">{{this.user.person.firstName}}
            {{this.user.person.lastName}}</div>
        </div>
      </div>
      <nav class="flex flex-col space-y-1 text-sm font-medium">
        <LinkTo
          @route="authenticated.profile"
          class="flex items-center px-2 py-1 rounded text-white/90 hover:bg-gray-50 hover:text-midnight transition"
        >
          <PhUserCircle @size="16" class="mr-2" />
          {{t "sidebar.account_information"}}
        </LinkTo>
        {{#unless this.isFamilyMember}}
          {{#each this.activeRoles as |role|}}
            <button
              type="button"
              class="flex items-center text-left px-2 py-1 rounded transition
                {{if
                  (eq this.currentRole.relationshipType role.relationshipType)
                  'bg-white/10 text-white'
                  'text-white/80 hover:bg-gray-50 hover:text-midnight'
                }}"
              {{on "click" (fn this.setCurrentRole role)}}
            >
              {{#if (eq this.currentRole.relationshipType role.relationshipType)}}
                <PhCaretRight @size="16" class="mr-2" />
              {{else}}
                <div class="w-4 h-4 mr-2"></div>
              {{/if}}
              {{t (concat "user-roles." (lowercase role.relationshipType))}}
            </button>
          {{/each}}
        {{/unless}}
        <LinkTo
          @route="logout"
          class="flex items-center px-2 py-1 rounded text-white/90 hover:bg-gray-50 hover:text-midnight transition"
        >
          <PhSignOut @size="16" class="mr-2" />
          {{t "header.user_menu.logout"}}
        </LinkTo>
      </nav>
    </div>

    {{! Desktop / larger screens keep dropdown }}
    <div class="hidden lg:block">
      <Dropdown as |d|>
        <d.Trigger
          @intent="default"
          @size="sm"
          @appearance="minimal"
          class="mb-2 w-full text-sm text-white text-left hover:bg-gray-50 hover:text-midnight px-1! py-1!"
        >
          <div class="flex items-center font-normal">
            <AppHeaderInitialsIcon @initials={{this.user.person.initials}} @size="sm" />
            <div class="align-middle ml-2 max-w-[140px] truncate">{{this.user.person.firstName}}
              {{this.user.person.lastName}}</div>
          </div>
        </d.Trigger>

        <d.Menu @renderInPlace={{true}} @intent="primary" class="text-gray-900" as |Item|>
          <Item @key="profile">
            <LinkTo @route="authenticated.profile" class="flex items-center">
              <PhUserCircle @size="16" class="mr-2" />
              {{t "sidebar.account_information"}}
            </LinkTo>
          </Item>
          {{#unless this.isFamilyMember}}
            {{#each this.activeRoles as |role index|}}
              <Item
                @key={{role.relationshipType}}
                @withDivider={{eq index (subtract this.activeRoles.length 1)}}
                @onClick={{fn this.setCurrentRole role}}
              >
                <div class="flex items-center">
                  {{#if (eq this.currentRole.relationshipType role.relationshipType)}}
                    <PhCaretRight @size="16" class="mr-2" />
                  {{else}}
                    <div class="opacity-0">
                      <PhCaretRight @size="16" class="mr-2" />
                    </div>
                  {{/if}}
                  {{t (concat "user-roles." (lowercase role.relationshipType))}}
                </div>
              </Item>
            {{/each}}
          {{/unless}}
          <Item @key="logout" @onClick={{this.sessionContext.logout}}>
            <div class="flex items-center">
              <PhSignOut @size="16" class="mr-2" />
              {{t "header.user_menu.logout"}}
            </div>
          </Item>
        </d.Menu>
      </Dropdown>
    </div>
  </template>
}
